// ================================================================
// src/services/roomeetAuth.js
// نسخه‌ی نهایی — کاملاً هماهنگ با PrivateClassShared (v1)
// ================================================================
// این ماژول مسئول:
//   ۱. لاگین مستقیم کاربر با شماره/رمز خودش
//   ۲. افزودن کاربر به Roomeet با admin API (اگر وجود نداشت)
//   ۳. سینک یکپارچه (login → add → login با retry)
//   ۴. re-export اعتبارنامه‌ها از PrivateClassShared (منبع واحد)
// ================================================================

import {
  RoomeetAPIService,
  getAdminRoomeetApi,
  getUserDisplayName,
  // ✅ منبع واحد اعتبارنامه‌ها — از PrivateClassShared ایمپورت می‌کنیم
  // تا کد تکراری حذف شود و همیشه همگام باشد
  saveRoomeetCreds,
  getRoomeetCreds,
  clearRoomeetCreds,
} from '../pages/PrivateClassShared';

// ✅ Re-export برای backward compatibility
// کدهای دیگر می‌توانند این‌ها را از هر دو مسیر ایمپورت کنند
export { saveRoomeetCreds, getRoomeetCreds, clearRoomeetCreds };

// ✅ کلید یکسان با PrivateClassShared
const AUTH_KEY = 'roomeet_auth_v1';

// ================================================================
// ۱. تلاش مستقیم: با شماره/رمز خود کاربر لاگین کن
// ================================================================
export async function tryRoomeetLogin(phone, password) {
  if (!phone || !password) return null;
  try {
    // persist: false, restore: false → هیچ اثری روی localStorage نگذارد؛
    // خودمان مدیریت می‌کنیم
    const api = new RoomeetAPIService({ persist: false, restore: false });
    const result = await api.login(phone, password);

    if (result?.token) {
      const school = result.schools?.[0] || {};
      const schoolId = school.schoolId || school._id || school.id || null;
      const role = school.role || null;

      // محاسبه‌ی expiresAt از JWT
      let expiresAt = null;
      try {
        const payload = JSON.parse(atob(result.token.split('.')[1]));
        if (payload.exp) expiresAt = payload.exp * 1000;
      } catch (_) {}

      // ذخیره با expiresAt تا AuthGate بتواند منقضی‌بودن را تشخیص دهد
      try {
        localStorage.setItem(
          AUTH_KEY,
          JSON.stringify({
            token: result.token,
            schoolId,
            userId: null,
            role,
            expiresAt,
          })
        );
      } catch (_) {}

      return {
        roomeetToken: result.token,
        schoolId,
        roomeetRole: role,
        roomeetPhone: phone,
      };
    }
  } catch (err) {
    // 401 عادی است — یعنی کاربر هنوز در Roomeet نیست
    if (!err.message?.includes('401')) {
      console.warn('[Roomeet] login failed:', err.message);
    }
  }
  return null;
}

// ================================================================
// ۲. افزودن کاربر جدید به Roomeet با admin API
// ================================================================
export async function ensureUserInRoomeetViaAdmin(appUser) {
  if (!appUser?.phone) {
    console.warn('[Roomeet] ensureUserInRoomeetViaAdmin: phone نیست');
    return null;
  }

  const adminApi = await getAdminRoomeetApi();
  if (!adminApi) {
    console.error(
      '[Roomeet] ❌ admin API آماده نیست — .env و نقش manager را چک کنید'
    );
    return null;
  }

  // نقش را برای Roomeet نرمال‌سازی کن (هر نقش غیر معلم → student)
  const role = appUser.role === 'teacher' ? 'teacher' : 'student';

  // دو فرمت شماره برای جستجو
  const phoneWithZero = appUser.phone.startsWith('0')
    ? appUser.phone
    : `0${appUser.phone}`;
  const phoneWithoutZero = appUser.phone.startsWith('0')
    ? appUser.phone.substring(1)
    : appUser.phone;

  // ============================================================
  // ۲-۱. جستجو — شاید کاربر از قبل عضو باشد
  // ============================================================
  try {
    const search = await adminApi.getSchoolMembers(1, 100, role, phoneWithoutZero);
    const found = search?.members?.find(
      (m) =>
        m.phone === appUser.phone ||
        m.phone === phoneWithZero ||
        m.phone === phoneWithoutZero
    );
    if (found?.memberId) {
      console.log(`[Roomeet] ✅ کاربر ${appUser.phone} از قبل هست: ${found.memberId}`);
      return found.memberId;
    }
  } catch (e) {
    console.warn('[Roomeet] search خطا:', e.message);
  }

  // ============================================================
  // ۲-۲. افزودن کاربر جدید
  // ============================================================
  try {
    const fullName = getUserDisplayName(appUser);
    console.log(`[Roomeet] 📤 افزودن "${fullName}" (${appUser.phone}) role=${role}`);

    const addResult = await adminApi.addUsersToSchool(
      [
        {
          name: fullName,
          phone: appUser.phone,
          password: appUser.password || '123456',
        },
      ],
      role
    );

    console.log('[Roomeet] 📥 پاسخ:', JSON.stringify(addResult, null, 2));

    const added = addResult?.users?.find((u) => u.error === false);
    if (added?.memberId) {
      console.log(`[Roomeet] ✅ کاربر اضافه شد: ${added.memberId}`);
      return added.memberId;
    }

    // اگر "قبلاً عضو بود" → دوباره سرچ کن
    if (addResult?.users?.[0]?.error === true) {
      console.log('[Roomeet] کاربر از قبل عضو بود — دوباره سرچ می‌کنم');
      const again = await adminApi.getSchoolMembers(1, 100, role, phoneWithoutZero);
      const found2 = again.members?.find(
        (m) =>
          m.phone === appUser.phone ||
          m.phone === phoneWithZero ||
          m.phone === phoneWithoutZero
      );
      if (found2?.memberId) {
        console.log(`[Roomeet] ✅ پیدا شد: ${found2.memberId}`);
        return found2.memberId;
      }
    }

    console.warn('[Roomeet] add result ناشناخته:', addResult);
    return null;
  } catch (e) {
    console.error('[Roomeet] add user خطا:', e.message);
    return null;
  }
}

// ================================================================
// ۳. لاگین با retry (Roomeet ممکن است بعد از add تأخیر داشته باشد)
// ================================================================
async function tryRoomeetLoginWithRetry(phone, password, maxAttempts = 3) {
  for (let i = 0; i < maxAttempts; i++) {
    const result = await tryRoomeetLogin(phone, password);
    if (result) return result;
    if (i < maxAttempts - 1) {
      console.log(`[Roomeet] 🔄 retry ${i + 1}/${maxAttempts - 1}...`);
      await new Promise((r) => setTimeout(r, 800));
    }
  }
  return null;
}

// ================================================================
// ۴. سینک یکپارچه — قلب سیستم
// ================================================================
export async function syncUserWithRoomeet(appUser, password) {
  if (!appUser?.phone || !password) {
    console.warn('[Roomeet] sync: phone یا password نیست');
    return null;
  }

  console.log(`[Roomeet] 🔄 سینک شروع برای ${appUser.phone} (${appUser.role})`);

  // ------------------------------------------------------------
  // مرحله ۱: تلاش لاگین مستقیم — کاربر ممکن است از قبل باشد
  // ------------------------------------------------------------
  console.log('[Roomeet] مرحله ۱: لاگین مستقیم');
  let result = await tryRoomeetLogin(appUser.phone, password);
  if (result) {
    console.log('[Roomeet] ✅ لاگین مستقیم موفق');
    return result;
  }
  console.log('[Roomeet] لاگین مستقیم نشد (کاربر جدید است)');

  // ------------------------------------------------------------
  // مرحله ۲: با admin API اضافه کن
  // ------------------------------------------------------------
  console.log('[Roomeet] مرحله ۲: افزودن با admin API');
  const memberId = await ensureUserInRoomeetViaAdmin({
    ...appUser,
    password,
  });

  if (!memberId) {
    console.error('[Roomeet] ❌❌❌ نتوانستم کاربر را اضافه کنم');
    console.error('[Roomeet] این معمولاً یعنی:');
    console.error('  - creds ادمین در .env نیست');
    console.error('  - یا شماره ادمین نقش manager ندارد');
    return null;
  }
  console.log('[Roomeet] ✅ کاربر اضافه شد:', memberId);

  // ------------------------------------------------------------
  // مرحله ۳: لاگین با retry (Roomeet ممکن است تأخیر داشته باشد)
  // ------------------------------------------------------------
  console.log('[Roomeet] مرحله ۳: لاگین با retry');
  result = await tryRoomeetLoginWithRetry(appUser.phone, password, 3);

  if (result) {
    console.log('[Roomeet] ✅✅✅ سینک کامل موفق');
    return result;
  }

  // کاربر در Roomeet اضافه شد ولی نتوانستیم لاگین کنیم
  console.warn('[Roomeet] ⚠️ کاربر اضافه شد ولی توکن نگرفت');
  console.warn('[Roomeet] 👉 کاربر می‌تواند از AuthGate لاگین کند');
  return {
    roomeetToken: null,
    schoolId: null,
    roomeetRole: appUser.role,
    roomeetPhone: appUser.phone,
    roomeetMemberId: memberId,
    pendingLogin: true, // علامت که توکن نگرفته
  };
}

// ================================================================
// ۵. Helpers (برای AuthContext / Login component)
// ================================================================

// ذخیره‌ی امن کاربر (بدون password)
export function persistUser(user) {
  if (!user) return null;
  const { password, ...safeUser } = user;
  try {
    localStorage.setItem('user', JSON.stringify(safeUser));
  } catch (_) {}
  return safeUser;
}

// timeout برای جلوگیری از hang شدن سینک (کل flow طولانی است)
export function withTimeout(promise, ms = 25000) {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Roomeet sync timeout')), ms)
    ),
  ]);
}

// ذخیره‌ی توکن Roomeet با expiresAt
export function saveRoomeetAuth({ token, schoolId, userId = null, role = null }) {
  if (!token || !schoolId) return;
  try {
    let expiresAt = null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.exp) expiresAt = payload.exp * 1000;
    } catch (_) {}
    localStorage.setItem(
      AUTH_KEY,
      JSON.stringify({ token, schoolId, userId, role, expiresAt })
    );
  } catch (_) {}
}

// بازیابی توکن Roomeet با چک انقضا
export function getRoomeetAuth() {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.expiresAt && Date.now() >= parsed.expiresAt) {
      localStorage.removeItem(AUTH_KEY);
      return null;
    }
    return parsed;
  } catch (_) {
    return null;
  }
}

// پاک کردن توکن Roomeet
export function clearRoomeetAuth() {
  try {
    localStorage.removeItem(AUTH_KEY);
  } catch (_) {}
}